import type { TFunction } from 'i18next';
import isAlpha from 'validator/lib/isAlpha';
import isLength from 'validator/lib/isLength';
import isURL from 'validator/lib/isURL';
import * as Yup from 'yup';
import type { FormikPropsLinesRow } from '@/components/LinesRow/LinesRow';
import type { LoginType, ProjectType, RegistrationTypes, ValidateCompanyType } from '@/types';
import { checkEmail, checkPassword } from './functions';

declare module 'yup' {
    interface StringSchema {
        dropdownValidate(errorMessage: string): Yup.StringSchema;
    }
}

export const validateRegistartion = (t: TFunction) => (values: Partial<RegistrationTypes>) => {
    const errors: Partial<RegistrationTypes> = {};

    if (!values.email) {
        errors.email = t('helpers.email.require');
    } else if (!checkEmail(values.email)) {
        errors.email = t('helpers.email.invalid');
    }

    if (!values.first_name || !isLength(values.first_name, { min: 2, max: 25 })) {
        errors.first_name = t('helpers.name.require');
    } else if (!/^[A-ZА-ЯЁ]+$/i.test(values.first_name)) {
        errors.first_name = t('helpers.name.invalid');
    }

    if (!values.last_name || !isLength(values.last_name, { min: 2, max: 25 })) {
        errors.last_name = t('helpers.surname.require');
    } else if (!/^[A-ZА-ЯЁ]+$/i.test(values.last_name)) {
        errors.last_name = t('helpers.surname.invalid');
    }

    if (!values.password || values.password.length < 6) {
        errors.password = t('helpers.password.require');
    } else if (!checkPassword(values.password)) {
        errors.password = t('helpers.password.require');
    }

    if (!values.password_confirmation || values.password_confirmation !== values.password) {
        errors.password_confirmation = t('helpers.password.repeat');
    }

    return errors;
};
export const validateResetPassword = (t: TFunction) => (values: { password: string; password_confirmation: string }) => {
    const errors: Partial<{ password: string; password_confirmation: string }> = {};

    if (!values.password || values.password.length < 6) {
        errors.password = t('helpers.password.require');
    } else if (!checkPassword(values.password)) {
        errors.password = t('helpers.password.require');
    }

    if (!values.password_confirmation || values.password_confirmation !== values.password) {
        errors.password_confirmation = t('helpers.password.repeat');
    }

    return errors;
};
export const validateLogin = (t: TFunction) => (values: LoginType) => {
    const errors: Partial<LoginType> = {};

    if (!values.email) {
        errors.email = t('helpers.email.require');
    } else if (!checkEmail(values.email)) {
        errors.email = t('helpers.email.invalid');
    }

    if (!values.password) {
        errors.password = t('helpers.password.require');
    } else if (values.password.length < 6) {
        errors.password = t('helpers.password.require');
    }

    return errors;
};
export const validateForgotPassword = (t: TFunction) => (values: { email: string }) => {
    const errors: Partial<{ email: string }> = {};

    if (!values.email) {
        errors.email = t('helpers.email.require');
    } else if (!checkEmail(values.email)) {
        errors.email = t('helpers.email.invalid');
    }

    return errors;
};
export const validateEditProfile = (t: TFunction) => (values: Partial<RegistrationTypes>) => {
    const errors: Partial<RegistrationTypes> = {};

    if (!values.first_name) {
        errors.first_name = t('helpers.name.require');
    } else if (values.first_name.length < 2 || values.first_name.length > 20 || !isAlpha(values.first_name)) {
        errors.first_name = t('helpers.name.invalid');
    }

    if (!values.last_name) {
        errors.last_name = t('helpers.surname.require');
    } else if (values.last_name.length < 3 || values.last_name.length > 25 || !isAlpha(values.last_name)) {
        errors.last_name = t('helpers.surname.invalid');
    }

    return errors;
};
export const validateCreateCompany = (t: TFunction) => (values: ValidateCompanyType) => {
    const errors: Partial<ValidateCompanyType> = {};

    if (!values.name || values.name.trim().length < 2) {
        errors.name = t('helpers.name.require-symbol');
    }

    if (!values.site) {
        errors.site = t('helpers.site');
    } else if (!isURL(values.site)) {
        errors.site = t('helpers.site');
    }

    return errors;
};
export const validateAddEmployeesSchema = (t: TFunction) =>
    Yup.object().shape({
        employees: Yup.array().of(
            Yup.object().shape({
                email: Yup.string().email(t('helpers.email.invalid')).required(t('helpers.email.require')),
                projects: Yup.array().of(
                    Yup.object().shape({
                        role_id: Yup.number().required(t('helpers.roles.project')),
                    }),
                ),
            }),
        ),
    });
export const validateCreateProject = (t: TFunction) => (values: ProjectType) => {
    const errors: Partial<Record<keyof ProjectType, string>> = {};

    if (!values.title || values.title.trim().length < 3) {
        errors.title = t('helpers.project-name');
    }
    if (!values.genre || values.genre.trim().length < 3) {
        errors.genre = t('helpers.project-genre');
    }
    if (!values.audience?.trim()) {
        errors.audience = t('helpers.project-audience');
    }

    if (!values.act_quantity) {
        errors.act_quantity = t('helpers.project-act-quantity');
    }

    return errors;
};
export const validateEpisode = (t: TFunction) => (values: { title: string }) => {
    const errors: { [key: string]: string } = {};

    if (!values.title) {
        errors.title = t('helpers.not-empty');
    }

    return errors;
};

export const validateEpisodeSynopsis = (t: TFunction) => (values: Partial<{ logline: string; synopsis: string }>) => {
    const errors: Partial<{ logline: string; synopsis: string }> = {};

    if (!values.logline) {
        errors.logline = t('helpers.not-empty');
    }

    if (values.logline && values.logline.trim().length > 300) {
        errors.logline = t('helpers.not-more-300');
    }

    return errors;
};

export const validateCreateNewLine = (t: TFunction) => (values: Partial<FormikPropsLinesRow>) => {
    const errors: { [key: string]: string } = {};

    if (!values.title || values.title.trim().length < 2) {
        errors.title = t('helpers.line-name');
    }

    if (!values.code || values.code.trim().length < 1) {
        errors.code = t('helpers.line-code');
    }

    if (!values.color) {
        errors.color = t('helpers.line-code');
    }

    return errors;
};

Yup.addMethod<Yup.StringSchema>(Yup.string, 'dropdownValidate', function (errorMessage) {
    return this.test(`dropdown-check`, errorMessage, function (value) {
        const { path, createError } = this;
        return (value && value !== 'none') || createError({ path, message: errorMessage });
    });
});

export const validateFeedbackSchema = (t: TFunction) =>
    Yup.object().shape({
        problem: Yup.string().dropdownValidate(t('feedback_form.error.problem')).required(),
        title: Yup.string().min(1).required(t('feedback_form.error.title')),
        comment: Yup.string().min(1).required(t('feedback_form.error.comment')),
    });
